import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar, AlertCircle, X, FileSignature } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUCKETS = [
  { key: 'urgent',  label: 'Expiring within 30 days', match: (d) => d <= 30 },
  { key: 'warning', label: 'Expiring in 31–90 days',  match: (d) => d > 30 && d <= 90 },
];

const statusClassFor = (status, days) => {
  if (status === 'expired' || days < 0) return 'renewal-expired';
  if (status === 'urgent'  || (days >= 0 && days <= 30)) return 'renewal-urgent';
  if (status === 'warning' || (days > 30 && days <= 90)) return 'renewal-warning';
  return 'renewal-normal';
};

const formatDocType = (renewal) => {
  if (renewal.kind === 'tenancy' || renewal.document_type === 'tenancy_end') {
    return 'TENANCY END';
  }
  return renewal.document_type.replace(/_/g, ' ').toUpperCase();
};

const daysLabel = (d) => (d < 0 ? `${Math.abs(d)} days overdue` : `${d} days left`);

export default function RenewalsTab() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);

  const filter = searchParams.get('filter');

  useEffect(() => {
    fetchRenewals();
  }, []);

  const fetchRenewals = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/renewals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRenewals(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load renewals');
      setLoading(false);
    }
  };

  const visibleRenewals = useMemo(() => {
    // Only show renewals up to 90 days away (including overdue). Anything
    // further out doesn't belong on this page.
    const within90 = renewals.filter(r => (r.days_remaining ?? 999) <= 90);
    if (filter === 'urgent') {
      return within90.filter(r => (r.days_remaining ?? 999) <= 30);
    }
    return within90;
  }, [renewals, filter]);

  const grouped = useMemo(() => {
    return BUCKETS.map(b => ({
      ...b,
      items: visibleRenewals.filter(r => b.match(r.days_remaining)),
    })).filter(g => g.items.length > 0);
  }, [visibleRenewals]);

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const clearFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('filter');
    setSearchParams(next);
  };

  const handleOpen = (renewal) => {
    if (renewal.kind === 'tenancy') {
      navigate(`/property/${renewal.property_id}`, { state: { from: '/renewals' } });
    } else {
      navigate(`/property/${renewal.property_id}`, { state: { openDocument: renewal.document_type, from: '/renewals' } });
    }
  };

  if (loading) {
    return <div className="tab-loading">Loading renewals...</div>;
  }

  return (
    <div className="renewals-tab">
      {filter === 'urgent' && (
        <div className="filter-chip-row" data-testid="renewals-active-filter">
          <span className="filter-chip filter-chip-urgent">
            <AlertCircle size={14} />
            Showing urgent &amp; overdue only
            <button
              type="button"
              onClick={clearFilter}
              className="filter-chip-clear"
              aria-label="Clear filter"
              data-testid="clear-renewals-filter"
            >
              <X size={14} />
            </button>
          </span>
        </div>
      )}

      {visibleRenewals.length === 0 ? (
        <div className="empty-state" data-testid="empty-renewals">
          <Calendar size={64} className="empty-icon" />
          <h3>{filter === 'urgent' ? 'No urgent renewals' : 'No renewals due'}</h3>
          <p>{filter === 'urgent' ? 'Nothing needs immediate attention.' : 'All your documents are up to date'}</p>
        </div>
      ) : (
        <div className="renewals-grouped">
          {grouped.map(group => (
            <section key={group.key} className={`renewal-group renewal-group-${group.key}`} data-testid={`renewal-group-${group.key}`}>
              <h3 className="renewal-group-label">
                {group.label}
                <span className="renewal-group-count">{group.items.length}</span>
              </h3>
              <div className="renewals-list">
                {group.items.map((renewal, index) => (
                  <div
                    key={`${renewal.kind || 'doc'}::${renewal.id}`}
                    className={`renewal-item ${statusClassFor(renewal.status, renewal.days_remaining)}`}
                    onClick={() => handleOpen(renewal)}
                    data-testid={`renewal-item-${group.key}-${index}`}
                  >
                    <div className="renewal-main">
                      <div className="renewal-doc-type" data-testid="renewal-doc-type">
                        {renewal.kind === 'tenancy' && <FileSignature size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px' }} />}
                        {formatDocType(renewal)}
                      </div>
                      <div className="renewal-address" data-testid="renewal-address">{renewal.property_address}</div>
                      <div className="renewal-date">Expires: {formatDate(renewal.expiry_date)}</div>
                    </div>
                    <div className="renewal-countdown">
                      <div className={`days-badge ${statusClassFor(renewal.status, renewal.days_remaining)}`} data-testid="days-remaining">
                        {daysLabel(renewal.days_remaining)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
